import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Storefront, Sparkle, Plus, X } from '@phosphor-icons/react';

const KINDS = ['trinket', 'voice_pack', 'skin', 'wish', 'collab'];

const fileToB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result || '').split(',')[1] || '');
  r.onerror = rej; r.readAsDataURL(file);
});

const CreateModal = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [kind, setKind] = useState('trinket');
  const [price, setPrice] = useState(10);
  const [imageB64, setImageB64] = useState(null);
  const [busy, setBusy] = useState(false);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4_000_000) return toast.error('5mb max');
    setImageB64(await fileToB64(f));
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/market/listings', {
        title: title.trim(), description: desc.trim(),
        kind, price_resonance: price, image_b64: imageB64,
      });
      toast.success('Listed.');
      onCreated?.(); onClose?.();
      setTitle(''); setDesc(''); setImageB64(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not');
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-xl grid place-items-center px-4" onClick={onClose}>
      <div className="w-full max-w-md glass rounded-3xl p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-white/60">
          <X size={18} weight="bold" />
        </button>
        <div className="text-center mb-4">
          <Storefront size={28} weight="duotone" className="text-bx-cyan mx-auto" />
          <div className="font-display font-bold text-xl mt-2">List something rare</div>
          <div className="text-xs text-white/65">Other creators redeem with Resonance.</div>
        </div>
        <input data-testid="mkt-title" value={title} onChange={(e) => setTitle(e.target.value.slice(0, 60))}
          placeholder="What is it?" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm mb-2 focus:outline-none focus:border-bx-cyan" />
        <textarea data-testid="mkt-desc" value={desc} onChange={(e) => setDesc(e.target.value.slice(0, 240))}
          placeholder="What does it carry?" rows={3}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm mb-2 focus:outline-none focus:border-bx-cyan" />
        <div className="flex gap-2 mb-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            data-testid="mkt-kind"
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none">
            {KINDS.map((k) => (<option key={k} value={k}>{k}</option>))}
          </select>
          <input type="number" min="1" max="10000"
            data-testid="mkt-price"
            value={price} onChange={(e) => setPrice(Math.max(1, parseInt(e.target.value || '1')))}
            className="w-28 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-bx-volt font-bold focus:outline-none" />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">cover (optional)</div>
        <input type="file" accept="image/*" onChange={onPhoto} data-testid="mkt-img"
          className="text-xs text-white/65 mb-3" />
        {imageB64 && <img alt="" src={`data:image/png;base64,${imageB64}`} className="rounded-xl max-h-32 mb-2" />}
        <button data-testid="mkt-create" onClick={submit}
          disabled={busy || !title.trim() || !desc.trim()}
          className="w-full btn-volt py-3 rounded-full font-bold text-sm">
          {busy ? 'listing…' : 'List for ' + price + ' ✦'}
        </button>
      </div>
    </div>
  );
};

const Marketplace = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/market/listings');
      setListings(data.listings || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const redeem = async (id, price) => {
    if (!window.confirm(`Spend ${price} Resonance?`)) return;
    try {
      await api.post(`/market/listings/${id}/redeem`);
      toast.success('Yours.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not redeem');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-bx-cyan">creator-to-creator</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Resonance Market</h1>
          <p className="text-white/60 mt-2 text-sm max-w-xl">
            Creators list rare items, voice packs, skins, wishes, collabs.
            Others redeem with Resonance earned in the world.
          </p>
        </div>
        <button data-testid="open-mkt-create"
          onClick={() => setOpenCreate(true)}
          className="btn-volt px-5 py-3 rounded-full text-sm font-bold flex items-center gap-2">
          <Plus size={14} weight="bold" /> List something
        </button>
      </div>

      {loading ? (
        <div className="text-white/60">listening…</div>
      ) : listings.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center">
          <div className="text-white/65">Empty market. Be the first to list.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map((l) => (
            <div key={l.id} data-testid={`mkt-listing-${l.id}`} className="glass rounded-2xl p-4">
              {l.image_url && (
                <img alt="" src={`${process.env.REACT_APP_BACKEND_URL}${l.image_url}`}
                  className="w-full h-32 object-cover rounded-xl mb-3" />
              )}
              <div className="text-[10px] uppercase tracking-widest text-bx-cyan">{l.kind}</div>
              <div className="font-display font-bold text-lg mt-1">{l.title}</div>
              <div className="text-xs text-white/60 mt-1 line-clamp-3">{l.description}</div>
              <div className="flex items-center justify-between mt-3">
                <Link to={`/creator/${l.seller_username}`} className="text-[10px] text-white/60 hover:text-white">@{l.seller_username}</Link>
                <button data-testid={`mkt-redeem-${l.id}`} onClick={() => redeem(l.id, l.price_resonance)}
                  className="px-3 py-1.5 rounded-full bg-bx-volt text-black text-xs font-bold">
                  ✦ {l.price_resonance}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateModal open={openCreate} onClose={() => setOpenCreate(false)} onCreated={load} />
    </div>
  );
};

export default Marketplace;
